import client from "services/ngage/api-client";
import {Article, ArticleData} from "types/Article";
import deserializeArticle from "services/ngage/deserializers/article";
import DataResource from "types/DataResource";
import {snakeKeys} from "services/array";

export type ArticleFilters = {
    search?: string | null
    published?: boolean
    page?: number
    perPage?: number
    sites?: string
}

/**
 * Get all articles that match the given filters (paginated)
 */
const filtered = async (filters: ArticleFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))

    const page = (await client.get<DataResource<Article>>('/articles', {params})).data

    if (page) page.data = page.data.map(deserializeArticle)

    return page
}

/**
 * Get a single article
 *
 * @param {number} articleId
 */
const get = async (articleId: number) => {
    try {
        const article = (await client.get<Article | null>(`/articles/${articleId}`)).data
        return article ? deserializeArticle(article) : null
    } catch (error) {
        console.error(`Unable to fetch article ${articleId}`, error)
        return null
    }
}

/**
 * Store a new article
 *
 * @param data
 */
const store = async (data: ArticleData) => {
    try {
        const article = (await client.post<Article | null>('/articles', data)).data
        return article ? deserializeArticle(article) : null
    } catch (error) {
        console.error("Unable to store article", error)
        throw error
    }
}

/**
 * Update an existing article
 *
 * @param {number} articleId
 * @param data
 */
const update = async (articleId: number, data: ArticleData) => {
    try {
        const article = (await client.put<Article | null>(`/articles/${articleId}`, data)).data
        return article ? deserializeArticle(article) : null
    } catch (error) {
        console.error(`Unable to update article ${articleId}`, error)
        throw error
    }
}

/**
 * Permanently delete a article
 *
 * @param {number} articleId
 */
const destroy = async (articleId: number) => {
    try {
        return (await client.delete<null>(`/articles/${articleId}`)).status === 204
    } catch (error) {
        console.error(`Unable to delete article ${articleId}`, error)
        throw error
    }
}

const articles = {
    filtered,
    get,
    store,
    update,
    destroy
}

export default articles;